/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2024-02-14 00:00:00
 *
 *******************************************************/
import _ from 'lodash';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Page, Button, Layout, Card, Badge, Icon, Link } from '@shopify/polaris';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import Select from 'react-select';

import TitleHelp from 'src/components/views/common/title-help';
import { URL } from 'src/constants/links';
import RefreshIcon from 'src/assets/images/common/refresh.svg';

import { routes } from 'src/routes';
import history from 'src/routes/history';
// import { getShop } from 'src/helpers';
import { trackFSEvent } from 'src/integrations/fullStory';
import { isThemeTurbo, hasOldThemeVersion } from 'src/helpers/theme-setup-helper';

import * as shopifyIntegrationThemeAction from 'actions/shopify-integration/theme-actions';
import * as themeIntegrationAction from 'actions/shopify-integration/theme-integration-actions';
import * as themeSetupV3Action from 'actions/theme-setup-v3-actions';
import { checkUserEnableShowAppVersionSwitching } from 'src/helpers/account-helper';

import { ExternalMinor } from '@shopify/polaris-icons';
import handIcon from 'src/assets/images/common/hand.svg';
import settingIcon from 'src/assets/images/common/setting.svg';
import templateIcon from 'src/assets/images/common/template.svg';

import ThemeTurboWarning, {
  WarningType
} from 'src/components/views/theme-setup-v3/theme-setup-form/theme-turbo-warning';
import RemoveOldVersionConfirmModal from './modal/remove-old-version-confirm.modal';

type Props = {};
type ComposedProps = Props & WithI18nProps;
type State = {
  activeModalRemoveOldVersionConfirm: boolean,
  refreshingThemeList: boolean
};

class ShopifyIntegrationScreenList extends Component<ComposedProps, State> {
  state = {
    refreshingThemeList: false,
    activeModalRemoveOldVersionConfirm: false
  };

  async componentDidMount() {
    const { getAllThemes } = this.props;
    getAllThemes();
  }

  componentWillReceiveProps(nextProps) {
    const {
      getAllThemesLoading: getAllThemesLoadingPrev,
      tenantApp,
      updateFieldReducer,
      removeOldThemeLoading: removeOldThemeLoadingPrev
    } = this.props;
    const enableAppVersionSwitching = checkUserEnableShowAppVersionSwitching(tenantApp);

    let { getAllThemesLoading, selectedThemeId, themes } = nextProps;
    if (!getAllThemesLoading && getAllThemesLoadingPrev) {
      // Finish loading list themes, check if selectedThemeId is available
      if (selectedThemeId) {
        const index = _.findIndex(themes, theme => theme.id === selectedThemeId);
        if (index === -1) {
          selectedThemeId = undefined;
        }
      }
      if (!selectedThemeId && !enableAppVersionSwitching) {
        const liveTheme = _.find(themes, theme => theme.role === 'main');
        updateFieldReducer('selectedThemeId', liveTheme?.id);
      }
    }

    if (
      removeOldThemeLoadingPrev &&
      !nextProps.removeOldThemeLoading &&
      nextProps.removeOldThemeSuccess
    ) {
      this.setState({ activeModalRemoveOldVersionConfirm: false });
    }
  }

  handleSelectTheme = selectedTheme => {
    const { updateFieldReducer, checkThemeWithTurbo, tenantApp } = this.props;
    const enableAppVersionSwitching = checkUserEnableShowAppVersionSwitching(tenantApp);

    updateFieldReducer('selectedThemeId', selectedTheme?.value);

    if (enableAppVersionSwitching) checkThemeWithTurbo(selectedTheme?.value);
  };

  onRemoveOldVersion = () => {
    this.setState({ activeModalRemoveOldVersionConfirm: true });
  };

  handleRemove = () => {
    const { removeOldVersionTheme, themeCheck, themeCheckId } = this.props;
    removeOldVersionTheme({
      themeId: themeCheckId,
      theme: themeCheck
    });
  };

  handleCancel = () => {
    this.setState({ activeModalRemoveOldVersionConfirm: false });
  };

  render() {
    const { refreshingThemeList, activeModalRemoveOldVersionConfirm } = this.state;
    const {
      i18n,
      themes,
      getAllThemesLoading,
      checkingTheme,
      selectedThemeId,
      tenantApp,
      checkThemeLoading,
      themeCheck,
      removeOldThemeLoading
    } = this.props;
    const enableAppVersionSwitching = checkUserEnableShowAppVersionSwitching(tenantApp);

    // Move live theme to the top
    const allThemesCloned = [...themes];
    allThemesCloned.sort((a, b) => a.name.trim().localeCompare(b.name.trim()));

    // Move live theme on top of list
    const liveThemeIndex = allThemesCloned.findIndex(theme => theme.role === 'main');
    if (liveThemeIndex !== -1) {
      const liveTheme = { ...allThemesCloned[liveThemeIndex] };
      allThemesCloned.splice(liveThemeIndex, 1);
      allThemesCloned.unshift(liveTheme);
    }

    const themesList = allThemesCloned
      ? allThemesCloned.map(item => {
          const { id } = item;

          const label = item?.name ? (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <p className="text-default">
                  {item?.name} (OS {item?.isOS20 ? '2.0' : '1.0'})
                </p>
              </div>
              <div>
                {item.role === 'main' ? (
                  <span id="ssr-selected-theme-item" className="live">
                    <Badge>
                      {i18n.translate('AdminHTML.ShopifyIntegration.ThemeStatus.liveTheme')}
                    </Badge>
                  </span>
                ) : (
                  <span id="ssr-selected-theme-item" className="unpublished">
                    <Badge>
                      {i18n.translate('AdminHTML.ShopifyIntegration.ThemeStatus.unpublishedTheme')}
                    </Badge>
                  </span>
                )}
              </div>
            </div>
          ) : (
            ''
          );
          return {
            ...item,
            value: id,
            label,
            name: item.name
          };
        })
      : [];

    const currentThemeSelected = themesList.find(theme => theme.value === selectedThemeId);

    const themeFullInfo = _.assign(_.clone(currentThemeSelected), _.clone(themeCheck));

    let warningThemeInfo = null;

    // https://apphub-workspace.slack.com/archives/C06CRR8RJSU/p1714009081431069?thread_ts=1713344365.084669&cid=C06CRR8RJSU
    // Live theme not install any version + Live theme install old version (v1,v2,v3)
    if (
      !checkThemeLoading &&
      themeFullInfo?.role === 'main' &&
      (hasOldThemeVersion(themeFullInfo) ||
        (!isThemeTurbo(themeFullInfo) && !hasOldThemeVersion(themeFullInfo)))
    ) {
      warningThemeInfo = (
        <ThemeTurboWarning
          tenantApp={tenantApp}
          type={WarningType.IN_SHOPIFY_INTEGRATION_THEME_LIVE}
        />
      );
    } else if (
      !checkThemeLoading &&
      themeFullInfo &&
      hasOldThemeVersion(themeFullInfo) &&
      !isThemeTurbo(themeFullInfo)
    ) {
      warningThemeInfo = (
        <ThemeTurboWarning
          tenantApp={tenantApp}
          type={WarningType.IN_SHOPIFY_INTEGRATION_THEME_V3}
          onRemoveOldVersion={this.onRemoveOldVersion}
        />
      );
    } else if (
      !checkThemeLoading &&
      themeFullInfo &&
      hasOldThemeVersion(themeFullInfo) &&
      isThemeTurbo(themeFullInfo)
    ) {
      warningThemeInfo = (
        <ThemeTurboWarning
          tenantApp={tenantApp}
          type={WarningType.IN_SHOPIFY_INTEGRATION_THEME_V3_MIXED_TURBO}
          onRemoveOldVersion={this.onRemoveOldVersion}
        />
      );
    }

    return (
      <Page title={i18n.translate('AdminHTML.ShopifyIntegration.Integration.pageTitle')}>
        <TitleHelp
          i18n={i18n}
          hrefUserManual={URL['Link_V2_Theme_ThemeSetup_ManualGuide']}
          // hrefFAQ={URL['ThemeSetupV3Doc_FAQ']}
        />

        <div className="main-layout">
          <Layout>
            <Layout.Section>
              {enableAppVersionSwitching ? (
                <Card
                  title={
                    <div className="cardTitle">
                      <span className="Polaris-Heading">
                        {i18n.translate('AdminHTML.ShopifyIntegration.Integration.selectTheme')}
                      </span>
                    </div>
                  }>
                  <Card.Section>
                    <div className="BC-Shopify-Integration_Select-Theme-Form">
                      <div className="select-theme">
                        <Select
                          selectProps={{ i18n }}
                          options={themesList}
                          value={currentThemeSelected}
                          onChange={this.handleSelectTheme}
                          placeholder={
                            getAllThemesLoading
                              ? i18n.translate(
                                  'AdminHTML.ShopifyIntegration.Integration.loadingTheme'
                                )
                              : !getAllThemesLoading && _.isEmpty(themesList)
                              ? i18n.translate(
                                  'AdminHTML.ThemeSetup.NewThemeSetup.step1_Theme_PlaceholderEmpty'
                                )
                              : i18n.translate(
                                  'AdminHTML.ThemeSetup.NewThemeSetup.step1_Theme_Placeholder2'
                                )
                          }
                          maxMenuHeight={212}
                          menuPortalTarget={document.body}
                          classNamePrefix="BC-Select-Integration"
                          isDisabled={
                            _.isEmpty(themesList) ||
                            getAllThemesLoading ||
                            refreshingThemeList ||
                            checkThemeLoading
                          }
                          noOptionsMessage={() =>
                            i18n.translate('AdminHTML.Common.Empty.reactSelect_NoOptions')
                          }
                          isOptionDisabled={option => option.disabled}
                          components={{
                            SingleValue: ({ data }) => {
                              return (
                                <div className="selected-theme-brian">
                                  <div>{data?.label}</div>
                                </div>
                              );
                            }
                          }}
                          menuPosition="absolute"
                          isSearchable={true}
                          filterOption={(candidate, input) => {
                            return candidate?.data?.name
                              ?.trim()
                              ?.toLowerCase()
                              .includes(input.toLowerCase());
                          }}
                        />

                        <img
                          className={`img-new-tab select-theme-refresh ${
                            getAllThemesLoading || refreshingThemeList || checkThemeLoading
                              ? 'refreshing'
                              : ''
                          }`}
                          src={RefreshIcon}
                          alt="refresh"
                          onClick={this.onRefreshThemeList}
                        />
                      </div>
                      {warningThemeInfo || <></>}
                      <div className="theme-turbo-guide">
                        <div className="p1">
                          <img src={handIcon} alt="warn" width={20} height={20} />
                          <p>{i18n.translate('AdminHTML.ThemeSetup.NewThemeSetup.turbo_label')}</p>
                        </div>
                        <div className="p2">
                          <Link external url={URL['BoostTemplateWidgets']}>
                            <img src={templateIcon} alt="setting" width={12} height={12} />
                            {i18n.translate(
                              'AdminHTML.ShopifyIntegration.SetupGuide.boostTemplateWidget'
                            )}
                            <Icon source={ExternalMinor} color="interactive" />
                          </Link>
                          <div className="line"></div>
                          <Link external url={URL['SetupWidgetForTheme']}>
                            <img src={settingIcon} alt="setting" width={12} height={12} />
                            {i18n.translate(
                              'AdminHTML.ShopifyIntegration.SetupGuide.setupWidgetForTheme'
                            )}
                            <Icon source={ExternalMinor} color="interactive" />
                          </Link>
                        </div>
                      </div>

                      <div className="btnNext">
                        <Button
                          primary
                          onClick={this.onNext}
                          disabled={
                            getAllThemesLoading ||
                            refreshingThemeList ||
                            _.isEmpty(themesList) ||
                            !themeCheck ||
                            checkThemeLoading ||
                            // Turbo switching not accept run on theme v1,v2,v3,empty. Only accept theme live has turbo
                            warningThemeInfo
                          }
                          loading={checkingTheme}>
                          {i18n.translate('AdminHTML.ShopifyIntegration.Actions.btnNext')}
                        </Button>
                      </div>
                    </div>
                  </Card.Section>
                </Card>
              ) : (
                <Card
                  title={
                    <div className="cardTitle">
                      <span className="Polaris-Heading">
                        {i18n.translate('AdminHTML.ShopifyIntegration.Integration.selectTheme')}
                      </span>
                    </div>
                  }>
                  <Card.Section>
                    <div className="BC-Shopify-Integration_Select-Theme-Form">
                      <div className="select-theme">
                        <Select
                          selectProps={{ i18n }}
                          options={themesList}
                          value={themesList.find(theme => theme.value === selectedThemeId)}
                          onChange={this.handleSelectTheme}
                          placeholder={
                            getAllThemesLoading
                              ? i18n.translate(
                                  'AdminHTML.ShopifyIntegration.Integration.loadingTheme'
                                )
                              : !getAllThemesLoading && _.isEmpty(themesList)
                              ? i18n.translate(
                                  'AdminHTML.ThemeSetup.NewThemeSetup.step1_Theme_PlaceholderEmpty'
                                )
                              : i18n.translate(
                                  'AdminHTML.ThemeSetup.NewThemeSetup.step1_Theme_Placeholder'
                                )
                          }
                          maxMenuHeight={212}
                          menuPortalTarget={document.body}
                          classNamePrefix="BC-Select-Integration"
                          isDisabled={
                            _.isEmpty(themesList) || getAllThemesLoading || refreshingThemeList
                          }
                          noOptionsMessage={() =>
                            i18n.translate('AdminHTML.Common.Empty.reactSelect_NoOptions')
                          }
                          isOptionDisabled={option => option.disabled}
                          components={{
                            SingleValue: ({ data }) => {
                              return (
                                <div className="selected-theme-brian">
                                  <div>{data?.label}</div>
                                </div>
                              );
                            }
                          }}
                          menuPosition="absolute"
                          isSearchable={true}
                          filterOption={(candidate, input) => {
                            return candidate?.data?.name
                              ?.trim()
                              ?.toLowerCase()
                              .includes(input.toLowerCase());
                          }}
                        />

                        <img
                          className={`img-new-tab select-theme-refresh ${
                            getAllThemesLoading || refreshingThemeList ? 'refreshing' : ''
                          }`}
                          src={RefreshIcon}
                          alt="refresh"
                          onClick={this.onRefreshThemeList}
                        />
                      </div>

                      <div className="btnNext">
                        <Button
                          primary
                          onClick={this.onNext}
                          disabled={
                            getAllThemesLoading || refreshingThemeList || _.isEmpty(themesList)
                          }
                          loading={checkingTheme}>
                          {i18n.translate('AdminHTML.ShopifyIntegration.Actions.btnNext')}
                        </Button>
                      </div>
                    </div>
                  </Card.Section>
                </Card>
              )}
            </Layout.Section>
          </Layout>
        </div>
        <RemoveOldVersionConfirmModal
          active={activeModalRemoveOldVersionConfirm}
          handleRemove={this.handleRemove}
          handleCancel={this.handleCancel}
          removeOldThemeLoading={removeOldThemeLoading}
        />
      </Page>
    );
  }

  onNext = async () => {
    const {
      themes,
      // handleChangeScreen,
      saveSelectedTheme,
      saveIntegratedTemplateInstance,
      checkThemeIntegrationStatus,
      selectedThemeId
    } = this.props;

    if (!selectedThemeId) {
      return;
    }

    // Call API to check integration status for specific theme
    const result = await checkThemeIntegrationStatus(selectedThemeId);

    // Get template instance from response data
    const integratedTemplateInstance = _.get(result, 'payload.data');

    // Save selected theme to state => Support page back and display theme details in other screen
    saveSelectedTheme(selectedThemeId);

    // Select a theme that hasn't linked any templates yet
    if (_.isEmpty(integratedTemplateInstance)) {
      history.push(routes.shopifyIntegration.newThemeWidget(selectedThemeId));
      // handleChangeScreen('new-template-integration');
      return;
    }

    // Select a theme that has linked a template
    saveIntegratedTemplateInstance(integratedTemplateInstance);
    // handleChangeScreen('update-template-integration');
    history.push(routes.shopifyIntegration.viewThemeWidget(selectedThemeId));

    // Track Full Story event
    const themeDetails = _.find(themes, { id: selectedThemeId });
    trackFSEvent('Select Theme Setup', {
      feature: 'SSR Theme Setup',
      selectedTheme: themeDetails?.name
    });
  };

  onRefreshThemeList = () => {
    const { getAllThemes, tenantApp, themeCheckId, checkThemeWithTurbo } = this.props;
    const enableAppVersionSwitching = checkUserEnableShowAppVersionSwitching(tenantApp);

    this.setState(
      {
        refreshingThemeList: true
      },
      () => {
        getAllThemes();
        if (enableAppVersionSwitching && themeCheckId) {
          checkThemeWithTurbo(themeCheckId);
        }
        setTimeout(() => {
          this.setState({
            refreshingThemeList: false
          });
        }, 1000);
      }
    );
  };
}

const mapStateToProps = (state, props) => {
  const { shopifyIntegrationTheme = {}, themeIntegration = {}, themeSetupV3 = {} } = state;

  const tenantApp = _.get(state.oauth, 'tenantApp', {});
  const themes = _.get(shopifyIntegrationTheme, 'allThemes', []);
  const selectedTheme = _.get(themeIntegration, 'selectedTheme', {});

  return {
    tenantApp,
    themes,
    getAllThemesLoading: _.get(shopifyIntegrationTheme, 'getAllThemesLoading', true),
    selectedTheme,
    selectedThemeId: _.get(themeIntegration, 'selectedThemeId', ''),
    // Check theme integration status
    checkingTheme: _.get(themeIntegration, 'checkingTheme', false),
    integratedTemplateInstance: _.get(themeIntegration, 'templateInstance', {}),
    checkThemeLoading: _.get(themeSetupV3, 'checkThemeLoading', false),
    themeCheck: _.get(themeSetupV3, 'themeCheck', null),
    themeCheckId: _.get(themeSetupV3, 'themeCheckId', null),

    removeOldThemeLoading: _.get(themeSetupV3, 'removeOldThemeloading', false),
    removeOldThemeSuccess: _.get(themeSetupV3, 'removeOldThemeSuccess', false),
    removeOldThemeError: _.get(themeSetupV3, 'removeOldThemeError', undefined),
    removeOldTheme: _.get(themeSetupV3, 'removeOldTheme', {})
  };
};
const mapDispatchToProps = (dispatch, ownProps) => ({
  getAllThemes: () => dispatch(shopifyIntegrationThemeAction.getAllThemes(ownProps.i18n)),
  checkThemeIntegrationStatus: themeId =>
    dispatch(themeIntegrationAction.checkThemeIntegrationStatus(ownProps.i18n, themeId)),
  updateFieldReducer: (fieldName, data) =>
    dispatch(themeIntegrationAction.handleUpdateField(fieldName, data)),
  checkThemeWithTurbo: themeId =>
    dispatch(themeSetupV3Action.checkThemeWithTurbo(themeId, ownProps.i18n)),
  removeOldVersionTheme: data =>
    dispatch(themeSetupV3Action.removeOldVersionTheme(data, ownProps.i18n, true))
});

export default withI18n()(
  connect(mapStateToProps, mapDispatchToProps)(ShopifyIntegrationScreenList)
);
